import React, { useState } from "react";
import { BANK_ACCOUNT_TYPE_OPTIONS, BANK_LINK_TYPE_OPTIONS } from "../constants/accountConstants";
import {
  canonicalBankAccountType,
  formatBankLabel,
} from "../utils/bankAccounts";
import { formatRoutingNumberInput } from "../utils/validationHelpers";

/**
 * Stateless, collapsible list of bank accounts linked to an investment account.
 *
 * Props:
 *   banks             : ordered bank objects (each with `id` or `tempId`).
 *   pendingIds        : { added: Set|Array, removed: Set|Array } — drives badges.
 *   isEditing         : enables edit affordances.
 *   disableUnlinkForTempIds : when true, freshly created (tempId) rows can't be unlinked.
 *   getFieldValue     : (baId, fieldPath, fallback) => value; form-state aware read.
 *   onEditField       : (baId, fieldPath, value) => void.
 *   onUnlink          : (baId) => void.
 *   onAdd             : () => void; when omitted, footer button hides.
 *   addLabel          : optional override for the footer button label.
 *   showAccountNumberMasked : masks the account number when read-only (default true).
 *   expandAll         : optional initial expansion override.
 */
const BankAccountAccordion = ({
  banks,
  pendingIds,
  isEditing = false,
  disableUnlinkForTempIds = true,
  fieldsReadOnly = false,
  getFieldValue,
  onEditField,
  onUnlink,
  onAdd,
  addLabel = "Link / Add Bank Account",
  showAccountNumberMasked = true,
  expandAll = false,
}) => {
  const fieldEditing = isEditing && !fieldsReadOnly;
  const [expanded, setExpanded] = useState(() => {
    if (!expandAll) return {};
    const state = {};
    (banks || []).forEach((b) => {
      state[b?.id || b?.tempId] = true;
    });
    return state;
  });

  const addedSet = toSet(pendingIds?.added);
  const removedSet = toSet(pendingIds?.removed);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const readValue = (baId, path, fallback) => {
    if (typeof getFieldValue === "function") return getFieldValue(baId, path, fallback);
    return fallback;
  };

  const maskAccountNumber = (num) => {
    if (!num) return "N/A";
    const s = String(num);
    if (s.length <= 4) return s;
    return `****${s.slice(-4)}`;
  };

  if (!banks || banks.length === 0) {
    return (
      <div>
        <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 mb-4">No bank account linked to this investment account.</p>
          {isEditing && onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium shadow-md shadow-emerald-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {addLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-3">
        {banks.map((ba) => {
          const key = ba.id || ba.tempId;
          const isNew = ba._isNew || !!ba.tempId;
          const isAdded = !isNew && addedSet.has(ba.id);
          const isRemoved = removedSet.has(ba.id);
          const badge = isRemoved
            ? { text: "Unlinking", cls: "bg-red-50 text-red-700 border-red-200" }
            : isNew
            ? { text: "New", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" }
            : isAdded
            ? { text: "Unsaved", cls: "bg-amber-50 text-amber-700 border-amber-200" }
            : null;

          const label = formatBankLabel({
            bank_name: readValue(key, "bank_name", ba.bank_name),
            account_number: readValue(key, "account_number", ba.account_number),
          });

          const canUnlink = isEditing && !(disableUnlinkForTempIds && isNew && !ba.id);

          return (
            <div key={key} className={`border rounded-lg ${isRemoved ? "border-red-200 bg-red-50/30" : "border-gray-200"}`}>
              <div className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors rounded-t-lg">
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">{label}</span>
                    {badge && (
                      <span className={`ml-2 inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${badge.cls}`}>
                        {badge.text}
                      </span>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  {canUnlink && onUnlink && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnlink(ba.id || ba.tempId);
                      }}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Unlink bank account"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  <button type="button" onClick={() => toggle(key)} className="p-1 text-gray-500 hover:text-gray-700">
                    <svg className={`w-5 h-5 transition-transform ${expanded[key] ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>

              {expanded[key] && (
                <div className="p-4 bg-white rounded-b-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <Field
                      label="Bank Name"
                      value={readValue(key, "bank_name", ba.bank_name)}
                      isEditing={fieldEditing}
                      onChange={(v) => onEditField && onEditField(key, "bank_name", v)}
                    />
                    <Field
                      label="Account Name"
                      value={readValue(key, "account_name", ba.account_name)}
                      isEditing={fieldEditing}
                      onChange={(v) => onEditField && onEditField(key, "account_name", v)}
                    />
                    <Field
                      label="Account Number"
                      value={readValue(key, "account_number", ba.account_number)}
                      displayValue={
                        !fieldEditing && showAccountNumberMasked
                          ? maskAccountNumber(ba.account_number)
                          : undefined
                      }
                      isEditing={fieldEditing}
                      onChange={(v) => onEditField && onEditField(key, "account_number", v)}
                    />
                    <Field
                      label="Routing Number"
                      value={readValue(key, "routing_number", ba.routing_number)}
                      isEditing={fieldEditing}
                      onChange={(v) => onEditField && onEditField(key, "routing_number", v)}
                      maxLength={9}
                      inputMode="numeric"
                      format={formatRoutingNumberInput}
                      placeholder="9 digits"
                    />
                    <SelectField
                      label="Account Type"
                      value={canonicalBankAccountType(
                        readValue(key, "account_type", ba.account_type),
                      )}
                      isEditing={fieldEditing}
                      options={BANK_ACCOUNT_TYPE_OPTIONS.map((o) => o.label)}
                      onChange={(v) => onEditField && onEditField(key, "account_type", v)}
                      formatDisplay={(v) => canonicalBankAccountType(v) || "N/A"}
                    />
                    <SelectField
                      label="Link Type"
                      value={readValue(key, "link_type", ba.link_type)}
                      isEditing={fieldEditing}
                      options={BANK_LINK_TYPE_OPTIONS}
                      onChange={(v) => onEditField && onEditField(key, "link_type", v)}
                      formatDisplay={(v) => v || "N/A"}
                    />
                    <CategoryField
                      label="Account Category"
                      value={readValue(key, "is_personal", ba.is_personal)}
                      isEditing={fieldEditing}
                      onChange={(v) => onEditField && onEditField(key, "is_personal", v)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isEditing && onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-4 w-full py-3 border-2 border-dashed border-emerald-200 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50 rounded-xl transition-all flex items-center justify-center gap-2 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {addLabel}
        </button>
      )}
    </div>
  );
};

const Field = ({
  label,
  value,
  displayValue,
  isEditing,
  onChange,
  maxLength,
  inputMode,
  format,
  placeholder,
}) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {isEditing ? (
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => {
          if (!onChange) return;
          const raw = e.target.value;
          const next = typeof format === "function" ? format(raw) : raw;
          onChange(next);
        }}
        maxLength={maxLength || undefined}
        inputMode={inputMode || undefined}
        placeholder={placeholder || undefined}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      />
    ) : (
      <p className="text-sm text-gray-900">{displayValue ?? (value || "N/A")}</p>
    )}
  </div>
);

const SelectField = ({ label, value, isEditing, options, onChange, formatDisplay }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {isEditing ? (
      <select
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      >
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    ) : (
      <p className="text-sm text-gray-900">{formatDisplay ? formatDisplay(value) : value || "N/A"}</p>
    )}
  </div>
);

const CategoryField = ({ label, value, isEditing, onChange }) => {
  const resolved = value === true || value === false ? value : null;
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {isEditing ? (
        <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-52">
          <button
            type="button"
            onClick={() => onChange && onChange(true)}
            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              resolved !== false ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Personal
          </button>
          <button
            type="button"
            onClick={() => onChange && onChange(false)}
            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              resolved === false ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Business
          </button>
        </div>
      ) : (
        <p className="text-sm text-gray-900">
          {resolved === true ? "Personal" : resolved === false ? "Business" : "N/A"}
        </p>
      )}
    </div>
  );
};

function toSet(input) {
  if (!input) return new Set();
  if (input instanceof Set) return input;
  if (Array.isArray(input)) return new Set(input);
  return new Set();
}

export default BankAccountAccordion;
